import { useEffect, useState } from "react";

/**
 * Custom hook which synchronizes some 'reactive' state with the browser's
 * session store. This is useful, for example, to restore the app's state after
 * after a page reload.
 * 
 * When the hook is called, it tries to load any existing data identified by the
 * given `key` from store. If there's none, it uses the provided `initialState`
 * instead. The returned tuple is similar to what `useState` would return.
 * 
 * @template T
 * @param {string} key - The name by which the state is to be looked up in the browser store.
 * @param {T} initialState - Any object properly serializable by `JSON.stringify`.
 * @returns {[T, React.Dispatch<React.SetStateAction<T>>]}}
 */
export default function useSessionState(key, initialState) {
    const [state, setState] = useState(() => {
        const existing = sessionStorage.getItem(key);
        return existing ? JSON.parse(existing) : initialState;
    });

    // Write state to store on any change
    useEffect(() => {
        sessionStorage.setItem(key, JSON.stringify(state));
    }, [state]);

    return [state, setState];
}
